import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  createPlatformInvoice,
  fetchAccountDetail,
  issuePlatformInvoice,
  markInvoicePaid,
  setAccountPlan,
  setAccountStatus,
} from "@/lib/platform-api";

export default function PlatformAccountDetail() {
  const { orgId } = useParams();
  const [data, setData] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({ description: "", amountUsd: 2800, plan: "studio" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetchAccountDetail(orgId);
      setData(res);
    } catch (err) {
      toast.error(err.message);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const createInvoice = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await createPlatformInvoice(orgId, invoiceForm);
      toast.success("Invoice created (draft)");
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const account = data?.account;
  const sub = data?.subscription;
  const usage = data?.usage || {};
  const invoices = data?.invoices || [];

  return (
    <div className="p-8 lg:p-12 max-w-[1100px] mx-auto">
      <Link to="/platform/accounts" className="text-xs text-[var(--text-tertiary)] hover:text-[var(--primary)]">
        ← Accounts
      </Link>
      <div className="text-overline mt-4">Account</div>
      <h1 className="font-heading text-3xl mt-2">{account?.name || orgId}</h1>
      <p className="text-sm text-[var(--text-secondary)] mt-1 font-mono">{orgId}</p>

      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <div className="surface p-5">
          <div className="text-overline">Status</div>
          <div className="font-mono text-xl mt-2 capitalize">{account?.status}</div>
          <div className="text-xs text-[var(--text-tertiary)] mt-2">Plan: {sub?.plan}</div>
        </div>
        <div className="surface p-5">
          <div className="text-overline">Usage</div>
          <div className="text-xs mt-2 space-y-1 font-mono">
            <div>Events: {usage.events?.used ?? 0} / {usage.events?.limit ?? "∞"}</div>
            <div>Emails: {usage.emails?.used ?? 0} / {usage.emails?.limit ?? "—"}</div>
          </div>
        </div>
        <div className="surface p-5 flex flex-col gap-2">
          <div className="text-overline">Actions</div>
          <select
            className="input-dark text-xs"
            defaultValue={sub?.plan || "studio"}
            onChange={async (e) => {
              try {
                await setAccountPlan(orgId, e.target.value);
                toast.success("Plan updated");
                load();
              } catch (err) {
                toast.error(err.message);
              }
            }}
          >
            {["starter", "studio", "scale", "enterprise"].map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <button type="button" className="btn-secondary text-xs" onClick={() => setAccountStatus(orgId, "suspended", "Ops suspend")}>
            Suspend
          </button>
          <button type="button" className="btn-ghost text-xs text-red-400" onClick={() => setAccountStatus(orgId, "churned", "Terminated")}>
            Terminate
          </button>
          <button type="button" className="btn-ghost text-xs" onClick={() => setAccountStatus(orgId, "active", "Reactivated")}>
            Reactivate
          </button>
        </div>
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <form onSubmit={createInvoice} className="surface p-6 space-y-3">
          <div className="text-overline">Create invoice</div>
          <input
            className="input-dark w-full text-sm"
            placeholder="Description"
            value={invoiceForm.description}
            onChange={(e) => setInvoiceForm((p) => ({ ...p, description: e.target.value }))}
          />
          <input
            type="number"
            className="input-dark w-full"
            placeholder="Amount USD"
            value={invoiceForm.amountUsd}
            onChange={(e) => setInvoiceForm((p) => ({ ...p, amountUsd: Number(e.target.value) }))}
          />
          <button type="submit" className="btn-primary text-sm" disabled={busy}>
            Create draft invoice
          </button>
        </form>

        <div className="surface p-6">
          <div className="text-overline mb-4">Invoices</div>
          <div className="space-y-3 max-h-[360px] overflow-y-auto">
            {invoices.length === 0 ? (
              <p className="text-sm text-[var(--text-tertiary)]">No invoices yet.</p>
            ) : (
              invoices.map((inv) => (
                <div key={inv.id} className="border border-[var(--border-default)] p-3 text-sm">
                  <div className="font-mono text-xs">{inv.invoiceNumber || inv.id}</div>
                  <div>{inv.description}</div>
                  <div className="text-xs text-[var(--text-tertiary)] mt-1">
                    ${inv.amountUsd?.toLocaleString()} · {inv.status}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {inv.status === "draft" ? (
                      <button type="button" className="btn-secondary text-xs" onClick={() => issuePlatformInvoice(orgId, inv.id).then(load)}>
                        Issue
                      </button>
                    ) : null}
                    {["issued", "pending", "overdue"].includes(inv.status) ? (
                      <button type="button" className="btn-primary text-xs" onClick={() => markInvoicePaid(orgId, inv.id).then(load)}>
                        Mark paid
                      </button>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
